import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Layout from "../../layout/Layout";
import { getOperatorDurusKpiAnalysis } from "../../../api/uretim/boruProfilDaily";

const TODAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Istanbul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(new Date());

const MONTH_START = `${TODAY.slice(0, 7)}-01`;

const PIE_COLORS = [
  "#f97316",
  "#0f766e",
  "#2563eb",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ca8a04",
  "#be123c",
];

const formatNumber = (value, digits = 0) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "0";
  }

  return numberValue.toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const getSeverity = (operator) => {
  const durus = Number(operator?.durus || 0);
  const hizPerformansi = Number(
    operator?.scadaHizPerformansYuzde || operator?.hizPerformansYuzde || 0
  );
  const hizKarsilastirmaSayisi = Number(
    operator?.scadaOrnekSayisi || operator?.hizKarsilastirmaSayisi || 0
  );

  if (durus >= 180 || (hizKarsilastirmaSayisi > 0 && hizPerformansi < 90)) {
    return {
      label: "Kritik",
      color: "#991b1b",
      bg: "#fee2e2",
    };
  }

  if (durus >= 120 || (hizKarsilastirmaSayisi > 0 && hizPerformansi < 100)) {
    return {
      label: "İzlenmeli",
      color: "#9a3412",
      bg: "#ffedd5",
    };
  }

  return {
    label: "Dengeli",
    color: "#166534",
    bg: "#dcfce7",
  };
};

function OperatorDurusKpiPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(MONTH_START);
  const [endDate, setEndDate] = useState(TODAY);
  const [makina, setMakina] = useState("");
  const [filterInfo, setFilterInfo] = useState(null);
  const [genel, setGenel] = useState(null);
  const [operatorBazli, setOperatorBazli] = useState([]);
  const [makinaBazli, setMakinaBazli] = useState([]);
  const [gunBazli, setGunBazli] = useState([]);
  const [durusSebebiBazli, setDurusSebebiBazli] = useState([]);
  const [scadaInfo, setScadaInfo] = useState(null);

  const loadData = useCallback(
    async (filters = {}) => {
      const selectedStartDate = filters.startDate ?? startDate;
      const selectedEndDate = filters.endDate ?? endDate;
      const selectedMakina = filters.makina ?? makina;

      if (!selectedStartDate || !selectedEndDate) {
        setError("Başlangıç ve bitiş tarihini seçmelisiniz.");
        return;
      }

      if (selectedStartDate > selectedEndDate) {
        setError("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await getOperatorDurusKpiAnalysis(
          selectedStartDate,
          selectedEndDate,
          selectedMakina
        );

        const payload = response?.data?.data || {};

        setFilterInfo(payload?.filter || null);
        setGenel(payload?.genel || {});
        setOperatorBazli(
          Array.isArray(payload?.operatorBazli) ? payload.operatorBazli : []
        );
        setMakinaBazli(
          Array.isArray(payload?.makinaBazli) ? payload.makinaBazli : []
        );
        setGunBazli(Array.isArray(payload?.gunBazli) ? payload.gunBazli : []);
        setDurusSebebiBazli(
          Array.isArray(payload?.durusSebebiBazli)
            ? payload.durusSebebiBazli
            : []
        );
        setScadaInfo(payload?.scada || null);
      } catch (requestError) {
        console.error("Operator duruş KPI verileri alınamadı:", requestError);
        setError(
          requestError?.response?.data?.message ||
            "Operator duruş KPI verileri alınamadı."
        );
        setFilterInfo(null);
        setGenel(null);
        setOperatorBazli([]);
        setMakinaBazli([]);
        setGunBazli([]);
        setDurusSebebiBazli([]);
        setScadaInfo(null);
      } finally {
        setLoading(false);
      }
    },
    [endDate, makina, startDate]
  );

  useEffect(() => {
    loadData({
      startDate: MONTH_START,
      endDate: TODAY,
      makina: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trendData = useMemo(
    () =>
      gunBazli.map((item) => ({
        label: item?.label || item?.tarih || "-",
        durus: Number(item?.durus || 0),
        tonaj: Number(item?.tonaj || 0),
        hedefHiz: Number(item?.ortalamaHedefHiz || 0),
        scadaHiz: Number(item?.scadaOrtalamaHiz || 0),
      })),
    [gunBazli]
  );

  const machineChartData = useMemo(
    () =>
      makinaBazli.map((item) => ({
        makina: item?.makinaLabel || `Makina ${item?.makinaAdi || "-"}`,
        durus: Number(item?.durus || 0),
        hedefHiz: Number(item?.ortalamaHedefHiz || 0),
        scadaHiz: Number(item?.scadaOrtalamaHiz || 0),
        hizFarki: Number(item?.scadaHizFarki || 0),
      })),
    [makinaBazli]
  );

  const reasonPieData = useMemo(
    () =>
      durusSebebiBazli
        .filter((item) => Number(item?.dakika || 0) > 0)
        .map((item, index) => ({
          name: item.reason,
          value: Number(item.dakika || 0),
          color: PIE_COLORS[index % PIE_COLORS.length],
        })),
    [durusSebebiBazli]
  );

  const operatorRows = useMemo(
    () =>
      [...operatorBazli]
        .map((item) => ({
          ...item,
          severity: getSeverity(item),
        }))
        .sort((a, b) => {
          if (Number(b.durus || 0) !== Number(a.durus || 0)) {
            return Number(b.durus || 0) - Number(a.durus || 0);
          }

          return (
            Number(a.scadaHizPerformansYuzde || 0) -
            Number(b.scadaHizPerformansYuzde || 0)
          );
        }),
    [operatorBazli]
  );

  const topOperatorsForChart = useMemo(
    () => operatorRows.slice(0, 8).reverse(),
    [operatorRows]
  );

  const highestRiskOperator = operatorRows[0] || null;

  const bestOperator = useMemo(() => {
    return [...operatorRows]
      .filter((item) => Number(item?.scadaOrnekSayisi || 0) > 0)
      .sort((a, b) => {
        if (
          Number(b.scadaHizPerformansYuzde || 0) !==
          Number(a.scadaHizPerformansYuzde || 0)
        ) {
          return (
            Number(b.scadaHizPerformansYuzde || 0) -
            Number(a.scadaHizPerformansYuzde || 0)
          );
        }

        return Number(a.durus || 0) - Number(b.durus || 0);
      })[0];
  }, [operatorRows]);

  const machineGapLeader = useMemo(() => {
    return [...makinaBazli].sort((a, b) => {
      return Number(a.scadaHizFarki || 0) - Number(b.scadaHizFarki || 0);
    })[0];
  }, [makinaBazli]);

  return (
    <Layout>
      <style>{`
        .operator-kpi-page {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .operator-kpi-hero {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          padding: clamp(20px, 3vw, 32px);
          background:
            radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 34%),
            linear-gradient(135deg, #0f172a 0%, #164e63 44%, #1d4ed8 100%);
          color: #fff;
          box-shadow: 0 22px 40px rgba(15, 23, 42, 0.18);
        }
        .operator-kpi-hero::after {
          content: "";
          position: absolute;
          inset: auto -70px -80px auto;
          width: 220px;
          height: 220px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
        }
        .operator-kpi-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.14);
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }
        .operator-kpi-title {
          font-size: clamp(28px, 4vw, 40px);
          line-height: 1.05;
          font-weight: 800;
          margin: 0 0 10px;
          max-width: 760px;
        }
        .operator-kpi-subtitle {
          margin: 0;
          max-width: 860px;
          color: rgba(255,255,255,0.84);
          font-size: 15px;
          line-height: 1.7;
        }
        .operator-kpi-filterbar {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          margin-top: 22px;
          position: relative;
          z-index: 1;
        }
        .operator-kpi-card {
          border: none;
          border-radius: 22px;
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }
        .operator-kpi-card .card-body {
          padding: 20px;
        }
        .operator-kpi-stat {
          height: 100%;
          background:
            linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%);
          border: 1px solid #e2e8f0;
        }
        .operator-kpi-stat-label {
          color: #475569;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 10px;
        }
        .operator-kpi-stat-value {
          color: #0f172a;
          font-size: clamp(28px, 3vw, 36px);
          font-weight: 800;
          line-height: 1;
          margin-bottom: 10px;
        }
        .operator-kpi-stat-note {
          color: #64748b;
          font-size: 13px;
          line-height: 1.6;
        }
        .operator-kpi-panel-title {
          color: #0f172a;
          font-size: 20px;
          font-weight: 800;
          margin: 0 0 6px;
        }
        .operator-kpi-panel-subtitle {
          color: #64748b;
          font-size: 13px;
          margin: 0 0 18px;
        }
        .operator-kpi-highlight {
          border-radius: 18px;
          padding: 16px;
          height: 100%;
          color: #fff;
          background: linear-gradient(135deg, #1d4ed8 0%, #0f766e 100%);
        }
        .operator-kpi-highlight.warning {
          background: linear-gradient(135deg, #c2410c 0%, #dc2626 100%);
        }
        .operator-kpi-highlight-label {
          opacity: 0.82;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 10px;
        }
        .operator-kpi-highlight strong {
          display: block;
          font-size: 24px;
          margin-bottom: 8px;
          line-height: 1.2;
        }
        .operator-kpi-highlight p {
          margin: 0;
          opacity: 0.92;
          line-height: 1.6;
          font-size: 14px;
        }
        .operator-kpi-table td,
        .operator-kpi-table th {
          vertical-align: middle;
        }
        .operator-kpi-risk-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 110px;
          padding: 7px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
        }
        @media (max-width: 768px) {
          .operator-kpi-card .card-body {
            padding: 16px;
          }
        }
      `}</style>

      <div className="operator-kpi-page">
        <section className="operator-kpi-hero">
          <div className="operator-kpi-eyebrow">Canlı Üretim KPI</div>
          <h1 className="operator-kpi-title">
            Operatör duruşları ile makina hızlarını aynı tarihler üzerinde
            birlikte karşılaştır
          </h1>
          <p className="operator-kpi-subtitle">
            Bu ekran artık manuel yazılan operatörlerle değil, seçtiğin tarih
            aralığında veritabanındaki gerçek üretim kayıtlarıyla çalışır.
            Duruş süresi, hız sapması, hedef tutma ve makina bazlı farklar tek
            panelde okunur.
          </p>

          <div className="operator-kpi-filterbar">
            <Form.Control
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(event) => setStartDate(event.target.value)}
            />

            <Form.Control
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={TODAY}
              onChange={(event) => setEndDate(event.target.value)}
            />

            <Form.Select
              value={makina}
              onChange={(event) => setMakina(event.target.value)}
            >
              <option value="">Tüm Makineler</option>
              <option value="1">Makina 1</option>
              <option value="2">Makina 2</option>
              <option value="3">Makina 3</option>
              <option value="4">Makina 4</option>
            </Form.Select>

            <Button
              variant="light"
              className="fw-bold"
              onClick={() =>
                loadData({
                  startDate,
                  endDate,
                  makina,
                })
              }
              disabled={loading}
            >
              {loading ? "Yükleniyor..." : "Karşılaştırmayı Yenile"}
            </Button>
          </div>
        </section>

        <Alert variant="info" className="mb-0">
          Operatör, duruş ve hız verileri seçilen günler için veritabanından
          çekilir. Gerçek makina hızları ayrıca
          {` `}
          <strong>`uretim/scada.js`</strong>
          {` `}
          üstünden gelen SCADA özetinden hesaplanır.
        </Alert>

        {scadaInfo && scadaInfo.success === false ? (
          <Alert variant="warning" className="mb-0">
            SCADA hız verisi alınamadı: {scadaInfo.message || "Bilinmeyen hata"}
          </Alert>
        ) : null}

        {error ? (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Card className="operator-kpi-card">
            <Card.Body className="d-flex align-items-center gap-3">
              <Spinner animation="border" size="sm" />
              <strong>Canlı üretim verileri yükleniyor...</strong>
            </Card.Body>
          </Card>
        ) : null}

        <Row className="g-3">
          <Col md={6} xl={3}>
            <Card className="operator-kpi-card operator-kpi-stat">
              <Card.Body>
                <div className="operator-kpi-stat-label">Toplam Duruş</div>
                <div className="operator-kpi-stat-value">
                  {formatNumber(genel?.toplamDurus || 0)} dk
                </div>
                <div className="operator-kpi-stat-note">
                  Çalışma oranı: %{formatNumber(genel?.calismaOraniYuzde || 0, 1)}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="operator-kpi-card operator-kpi-stat">
              <Card.Body>
                <div className="operator-kpi-stat-label">Ortalama Hız</div>
                <div className="operator-kpi-stat-value">
                  {formatNumber(genel?.scadaOrtalamaHiz || 0, 1)}
                </div>
                <div className="operator-kpi-stat-note">
                  SCADA örnek: {formatNumber(genel?.scadaOrnekSayisi || 0, 0)}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="operator-kpi-card operator-kpi-stat">
              <Card.Body>
                <div className="operator-kpi-stat-label">Hız Karşılama</div>
                <div className="operator-kpi-stat-value">
                  %{formatNumber(genel?.scadaHizPerformansYuzde || 0, 1)}
                </div>
                <div className="operator-kpi-stat-note">
                  Ortalama fark:{" "}
                  {Number(genel?.scadaHizFarki || 0) > 0 ? "+" : ""}
                  {formatNumber(genel?.scadaHizFarki || 0, 1)}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="operator-kpi-card operator-kpi-stat">
              <Card.Body>
                <div className="operator-kpi-stat-label">Kapsam</div>
                <div className="operator-kpi-stat-value">
                  {formatNumber(filterInfo?.rowCount || 0, 0)}
                </div>
                <div className="operator-kpi-stat-note">
                  {formatNumber(filterInfo?.operatorCount || 0, 0)} operatör,
                  {` `}
                  {formatNumber(gunBazli.length, 0)} gün
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={8}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <h3 className="operator-kpi-panel-title">
                  Gün bazlı duruş ve hız trendi
                </h3>
                <p className="operator-kpi-panel-subtitle">
                  Seçilen günlerde toplam duruş yükselirken hız hedefi ne kadar
                  korunmuş, SCADA ortalama hız ile birlikte okunur.
                </p>

                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "Toplam Duruş") {
                          return [`${formatNumber(value)} dk`, name];
                        }

                        if (name === "Tonaj") {
                          return [`${formatNumber(value)} kg`, name];
                        }

                        return [formatNumber(value, 1), name];
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="durus"
                      name="Toplam Duruş"
                      stroke="#dc2626"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="hedefHiz"
                      name="Hedef Hız"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="scadaHiz"
                      name="SCADA Hızı"
                      stroke="#0f766e"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <h3 className="operator-kpi-panel-title">
                  Duruş sebebi dağılımı
                </h3>
                <p className="operator-kpi-panel-subtitle">
                  En çok dakika kaybettiren sebep ilk aksiyon alanını gösterir.
                </p>

                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reasonPieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={62}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {reasonPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${formatNumber(value)} dk`} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="d-flex flex-column gap-2 mt-2">
                  {reasonPieData.slice(0, 6).map((item) => (
                    <div
                      key={item.name}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            display: "inline-block",
                            background: item.color,
                          }}
                        />
                        <span className="small text-muted">{item.name}</span>
                      </div>
                      <strong>{formatNumber(item.value)} dk</strong>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={5}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <h3 className="operator-kpi-panel-title">
                  Makina bazlı hız kıyası
                </h3>
                <p className="operator-kpi-panel-subtitle">
                  Hedef hız ile SCADA ortalama hızının makina bazlı farkı.
                </p>

                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={machineChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="makina" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => formatNumber(value, 1)} />
                    <Legend />
                    <Bar
                      dataKey="hedefHiz"
                      name="Hedef Hız"
                      fill="#93c5fd"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="scadaHiz"
                      name="SCADA Hızı"
                      fill="#0f766e"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <h3 className="operator-kpi-panel-title">
                  Operatör risk sıralaması
                </h3>
                <p className="operator-kpi-panel-subtitle">
                  Duruş süresi ve hız karşılama performansına göre ilk 8 operatör.
                </p>

                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={topOperatorsForChart}
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      dataKey="operator"
                      type="category"
                      tick={{ fontSize: 12 }}
                      width={100}
                    />
                    <Tooltip formatter={(value) => `${formatNumber(value)} dk`} />
                    <Legend />
                    <Bar
                      dataKey="durus"
                      name="Toplam Duruş"
                      radius={[0, 8, 8, 0]}
                    >
                      {topOperatorsForChart.map((entry) => (
                        <Cell
                          key={entry.operator}
                          fill={
                            Number(entry.scadaHizPerformansYuzde || 0) >= 100
                              ? "#0f766e"
                              : Number(entry.scadaHizPerformansYuzde || 0) >= 90
                              ? "#f97316"
                              : "#dc2626"
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md={6}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <div className="operator-kpi-highlight warning">
                  <div className="operator-kpi-highlight-label">
                    İlk aksiyon noktası
                  </div>
                  <strong>{highestRiskOperator?.operator || "-"}</strong>
                  <p>
                    {formatNumber(highestRiskOperator?.durus || 0)} dk duruş,
                    {` `}
                    {formatNumber(highestRiskOperator?.scadaOrtalamaHiz || 0, 1)}
                    {` `}
                    SCADA ort. hız ve{" "}
                    {highestRiskOperator?.enBuyukDurusSebebi?.reason || "-"} ile
                    bu aralıkta en yüksek risk burada görünüyor.
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="operator-kpi-card h-100">
              <Card.Body>
                <div className="operator-kpi-highlight">
                  <div className="operator-kpi-highlight-label">
                    Referans performans
                  </div>
                  <strong>{bestOperator?.operator || "-"}</strong>
                  <p>
                    %{formatNumber(bestOperator?.scadaHizPerformansYuzde || 0, 1)}
                    SCADA hız karşılama ve{" "}
                    {formatNumber(bestOperator?.durus || 0)} dk duruş ile sahaya
                    yayılabilecek en dengeli profil bu tarafta.
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="operator-kpi-card">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div>
                <h3 className="operator-kpi-panel-title mb-1">
                  Operatör detay tablosu
                </h3>
                <p className="operator-kpi-panel-subtitle mb-0">
                  Manuel yazılan liste yerine, veritabanından gelen gerçek
                  operatör kayıtları duruşla birlikte SCADA hız ortalamasıyla
                  gösterilir.
                </p>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Badge bg="dark">
                  {formatNumber(operatorRows.length, 0)} operatör
                </Badge>
                <Badge bg="secondary">
                  {filterInfo?.startDate || "-"} / {filterInfo?.endDate || "-"}
                </Badge>
              </div>
            </div>

            {operatorRows.length === 0 && !loading ? (
              <Alert variant="warning" className="mb-0">
                Seçilen tarih aralığında karşılaştırılacak operatör verisi
                bulunamadı.
              </Alert>
            ) : (
              <Table responsive hover className="align-middle operator-kpi-table mb-0">
                <thead>
                  <tr>
                    <th>Operatör</th>
                    <th>Toplam Duruş</th>
                    <th>Fiili</th>
                    <th>Ort. Hedef</th>
                    <th>SCADA Ort.</th>
                    <th>SCADA Farkı</th>
                    <th>SCADA Karşılama</th>
                    <th>Kayıt</th>
                    <th>Makinalar</th>
                    <th>En Büyük Sebep</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {operatorRows.map((item) => (
                    <tr key={item.operator}>
                      <td>
                        <strong>{item.operator}</strong>
                        <div className="text-muted small">{item.vardiyaText}</div>
                      </td>
                      <td>{formatNumber(item.durus)} dk</td>
                      <td>{formatNumber(item.fiili)} dk</td>
                      <td>{formatNumber(item.ortalamaHedefHiz, 1)}</td>
                      <td>{formatNumber(item.scadaOrtalamaHiz, 1)}</td>
                      <td>
                        {Number(item.scadaHizFarki || 0) > 0 ? "+" : ""}
                        {formatNumber(item.scadaHizFarki, 1)}
                      </td>
                      <td>%{formatNumber(item.scadaHizPerformansYuzde, 1)}</td>
                      <td>{formatNumber(item.kayitSayisi, 0)}</td>
                      <td>{item.makinaText || "-"}</td>
                      <td>
                        {item.enBuyukDurusSebebi
                          ? `${item.enBuyukDurusSebebi.reason} (${formatNumber(
                              item.enBuyukDurusSebebi.dakika
                            )} dk)`
                          : "-"}
                      </td>
                      <td>
                        <span
                          className="operator-kpi-risk-chip"
                          style={{
                            background: item.severity.bg,
                            color: item.severity.color,
                          }}
                        >
                          {item.severity.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Card className="operator-kpi-card">
          <Card.Body>
            <h3 className="operator-kpi-panel-title mb-1">
              Makina aksiyon özeti
            </h3>
            <p className="operator-kpi-panel-subtitle mb-0">
              Hedefe göre en çok geride kalan makina otomatik işaretlenir.
            </p>

            <div className="mt-3">
              <Alert variant="secondary" className="mb-0">
                {machineGapLeader?.makinaLabel || "-"} için SCADA ortalama hız farkı{" "}
                <strong>
                  {Number(machineGapLeader?.scadaHizFarki || 0) > 0 ? "+" : ""}
                  {formatNumber(machineGapLeader?.scadaHizFarki || 0, 1)}
                </strong>
                . Bu satır, duruş ve hız düşüşü birlikte okunarak ilk makina
                aksiyonunu işaret eder.
              </Alert>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
}

export default OperatorDurusKpiPage;
